import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Icon,
  alpha,
  useTheme,
} from "@mui/material";

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: "warning" | "error" | "info";
  isLoading?: boolean;
  details?: string[];
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  severity = "warning",
  isLoading = false,
  details = [],
}) => {
  const theme = useTheme();

  const getSeverityConfig = () => {
    switch (severity) {
      case "error":
        return {
          color: theme.palette.error.main,
          bgColor: alpha(theme.palette.error.main, 0.1),
          icon: "error",
        };
      case "info":
        return {
          color: theme.palette.info.main,
          bgColor: alpha(theme.palette.info.main, 0.1),
          icon: "info",
        };
      default:
        return {
          color: theme.palette.warning.main,
          bgColor: alpha(theme.palette.warning.main, 0.1),
          icon: "warning",
        };
    }
  };

  const config = getSeverityConfig();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: config.bgColor,
              color: config.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 24 }}>{config.icon}</Icon>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 3 }}>
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            mb: details.length > 0 ? 2 : 0,
            lineHeight: 1.6,
          }}
        >
          {message}
        </Typography>

        {details.length > 0 && (
          <Box
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              p: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            }}
          >
            {details.map((detail, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: index < details.length - 1 ? 1 : 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ fontSize: 16, color: "text.disabled" }}>
                  fiber_manual_record
                </Icon>
                {detail}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            bgcolor: config.color,
            "&:hover": {
              bgcolor: alpha(config.color, 0.8),
            },
          }}
          startIcon={
            isLoading ? (
              <Icon sx={{ fontSize: 18 }}>hourglass_empty</Icon>
            ) : undefined
          }
        >
          {isLoading ? "Processing..." : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
